import React, { useState, useEffect, useCallback } from 'react';
import { listLevels, saveLevel, deleteLevel, saveLevelsBatch } from '../lib/firestoreLevels';
import { loadSeedLevels } from '../lib/seedImport';
import { canExportToDisk, exportLevelsToDisk, downloadLevelsBundle } from '../lib/exportLevels';
import { defaultLevel } from '../lib/levelSchema';
import LevelEditor from '../components/LevelEditor';
import './LevelsPage.css';

const LevelsPage = () => {
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLevel, setEditingLevel] = useState(null);
  const [busy, setBusy] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setLevels(await listLevels());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const nextLevelId = () => (levels.length ? Math.max(...levels.map((l) => l.levelId)) + 1 : 1);
  const chapterFor = (id) => Math.floor((id - 1) / 100) + 1;

  const handleAddNew = () => {
    const id = nextLevelId();
    setEditingLevel(defaultLevel(id, chapterFor(id)));
  };

  const handleDuplicate = (level) => {
    const id = nextLevelId();
    const clone = JSON.parse(JSON.stringify(level));
    clone.levelId = id;
    clone.chapter = chapterFor(id);
    setEditingLevel(clone);
  };

  const handleSave = async (level) => {
    await saveLevel(level);
    setEditingLevel(null);
    await refresh();
  };

  const handleDelete = async (levelId) => {
    if (!window.confirm(`Xoá level ${levelId}? Không thể hoàn tác.`)) return;
    await deleteLevel(levelId);
    await refresh();
  };

  const handleImportSeed = async () => {
    if (levels.length > 0 && !window.confirm('Firestore đã có level. Import sẽ GHI ĐÈ các level trùng ID (theo seed campaign gốc). Tiếp tục?')) {
      return;
    }
    setBusy('import');
    try {
      const seed = await loadSeedLevels();
      await saveLevelsBatch(seed);
      await refresh();
      alert(`Đã import ${seed.length} level từ seed campaign.`);
    } catch (err) {
      alert('Import lỗi: ' + err.message);
    } finally {
      setBusy('');
    }
  };

  const handleExportDisk = async () => {
    setBusy('export');
    try {
      await exportLevelsToDisk(levels);
      alert('Đã ghi level_*.json + manifest.json vào thư mục đã chọn.');
    } catch (err) {
      if (err.name !== 'AbortError') alert('Export lỗi: ' + err.message);
    } finally {
      setBusy('');
    }
  };

  const handleExportDownload = () => {
    downloadLevelsBundle(levels);
  };

  if (loading) {
    return <div className="page-content">Loading levels...</div>;
  }

  if (editingLevel) {
    return (
      <div className="page-content">
        <h1>{levels.some((l) => l.levelId === editingLevel.levelId) ? `Edit level ${editingLevel.levelId}` : `New level ${editingLevel.levelId}`}</h1>
        <LevelEditor initialLevel={editingLevel} onSave={handleSave} onCancel={() => setEditingLevel(null)} />
      </div>
    );
  }

  const byChapter = new Map();
  for (const l of levels) {
    if (!byChapter.has(l.chapter)) byChapter.set(l.chapter, []);
    byChapter.get(l.chapter).push(l);
  }
  const chapters = [...byChapter.keys()].sort((a, b) => a - b);

  return (
    <div className="page-content">
      <h1>Game Levels</h1>
      <div className="actions-bar">
        <button className="btn btn-primary" onClick={handleAddNew}>+ Add New Level</button>
        <button className="btn" onClick={handleImportSeed} disabled={busy === 'import'}>
          {busy === 'import' ? 'Importing...' : '⬆️ Import seed levels'}
        </button>
        {canExportToDisk() && (
          <button className="btn" onClick={handleExportDisk} disabled={busy === 'export' || levels.length === 0}>
            {busy === 'export' ? 'Exporting...' : '📁 Export to assets/levels folder'}
          </button>
        )}
        <button className="btn" onClick={handleExportDownload} disabled={levels.length === 0}>
          ⬇️ Download JSON bundle
        </button>
      </div>

      {levels.length === 0 ? (
        <div className="table-container">
          <div className="empty-state">
            Chưa có level nào trong Firestore. Bấm "Import seed levels" để nạp 30 level campaign có sẵn, hoặc "Add New Level" để tạo mới.
          </div>
        </div>
      ) : (
        <div className="table-container">
          <table className="levels-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Mode</th>
                <th>Size</th>
                <th>Moves</th>
                <th>Goals</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {chapters.map((ch) => (
                <React.Fragment key={ch}>
                  <tr className="chapter-row"><td colSpan={6}>Chapter {ch}</td></tr>
                  {byChapter.get(ch).sort((a, b) => a.levelId - b.levelId).map((l) => (
                    <tr key={l.levelId}>
                      <td>{l.levelId}</td>
                      <td>{l.mode}</td>
                      <td>{l.width}×{l.height}</td>
                      <td>{l.moves}</td>
                      <td>{l.goals.map((g) => `${g.target}:${g.type}×${g.count}`).join(', ')}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn btn-primary" onClick={() => setEditingLevel(JSON.parse(JSON.stringify(l)))}>Edit</button>
                          <button className="btn" onClick={() => handleDuplicate(l)}>Duplicate</button>
                          <button className="btn btn-danger" onClick={() => handleDelete(l.levelId)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LevelsPage;
